import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getLeaves, getAttendance, getPayroll, getReviews, getEmployees } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, FileText, IndianRupee, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const EmployeeDashboard = ({ user, setUser }) => {
  const [stats, setStats] = useState({
    myLeaves: 0,
    myAttendance: 0,
    lastPayroll: null,
    lastReview: null
  });
  const [leaves, setLeaves] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const empRes = await getEmployees();
      const employees = empRes.data;
      const currentEmployee = employees.find(e => e.user_id === user?.id);

      if (!currentEmployee) {
        toast.error('Employee record not found');
        setLoading(false);
        return;
      }

      const results = await Promise.allSettled([
        getLeaves(currentEmployee.id),
        getAttendance(currentEmployee.id),
        getPayroll(currentEmployee.id),
        getReviews(currentEmployee.id)
      ]);

      const leavesData = results[0].status === 'fulfilled' ? results[0].value.data : [];
      const attendanceData = results[1].status === 'fulfilled' ? results[1].value.data : [];
      const payrollData = results[2].status === 'fulfilled' ? results[2].value.data : [];
      const reviewsData = results[3].status === 'fulfilled' ? results[3].value.data : [];

      setLeaves(leavesData);
      setAttendance(attendanceData);

      setStats({
        myLeaves: leavesData.length,
        myAttendance: Array.isArray(attendanceData) ? attendanceData.filter(a => a.status === 'present').length : 0,
        lastPayroll: Array.isArray(payrollData) && payrollData.length > 0 
          ? [...payrollData].sort((a, b) => {
              const dateA = (a.year || 0) * 100 + (a.month || 0);
              const dateB = (b.year || 0) * 100 + (b.month || 0);
              return dateB - dateA;
            })[0]
          : null,
        lastReview: Array.isArray(reviewsData) && reviewsData.length > 0 ? reviewsData[reviewsData.length - 1] : null
      });
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'My Leave Requests', value: stats.myLeaves, icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
    { title: 'Days Present', value: stats.myAttendance, icon: Calendar, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { title: 'Last Payroll', value: stats.lastPayroll ? `₹${stats.lastPayroll.net_salary?.toLocaleString('en-IN')}` : 'N/A', icon: IndianRupee, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { title: 'Last Review', value: stats.lastReview ? `${stats.lastReview.rating}/5` : 'N/A', icon: BarChart3, color: 'text-amber-600', bg: 'bg-amber-50' },
  ];

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-8" data-testid="employee-dashboard">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="dashboard-title">My Dashboard</h1>
          <p className="text-slate-600 mt-1">Your personal workspace overview</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border border-slate-100 shadow-sm hover:border-indigo-100 transition-colors card-hover" data-testid={`stat-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 font-medium">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-900 mt-2 mono" data-testid={`stat-value-${index}`}>{stat.value}</p>
                    </div>
                    <div className={`${stat.bg} ${stat.color} p-4 rounded-xl`}>
                      <Icon size={28} strokeWidth={1.5} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Leaves */}
        <Card className="border border-slate-100 shadow-sm" data-testid="recent-leaves-card">
          <CardHeader>
            <CardTitle>My Recent Leave Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {leaves.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No leave requests yet</p>
            ) : (
              <div className="space-y-3">
                {leaves.slice(0, 5).map((leave, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`leave-item-${index}`}>
                    <div>
                      <p className="font-medium text-slate-900 capitalize">{leave.leave_type.replace('_', ' ')} Leave</p>
                      <p className="text-sm text-slate-600">{leave.start_date} - {leave.end_date}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      leave.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                      leave.status === 'approved' ? 'bg-emerald-50 text-emerald-600' :
                      'bg-rose-50 text-rose-600'
                    }`} data-testid={`leave-status-${index}`}>
                      {leave.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Attendance */}
        <Card className="border border-slate-100 shadow-sm" data-testid="recent-attendance-card">
          <CardHeader>
            <CardTitle>My Recent Attendance</CardTitle>
          </CardHeader>
          <CardContent>
            {attendance.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No attendance records yet</p>
            ) : (
              <div className="space-y-3">
                {attendance.slice(0, 5).map((record, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`attendance-item-${index}`}>
                    <div>
                      <p className="font-medium text-slate-900">{record.date}</p>
                      <p className="text-sm text-slate-600">
                        {record.clock_in && `${record.clock_in} - ${record.clock_out || 'N/A'}`}
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      record.status === 'present' ? 'bg-emerald-50 text-emerald-600' :
                      record.status === 'absent' ? 'bg-rose-50 text-rose-600' :
                      record.status === 'half_day' ? 'bg-amber-50 text-amber-600' :
                      'bg-blue-50 text-blue-600'
                    }`} data-testid={`attendance-status-${index}`}>
                      {record.status.replace('_', ' ').toUpperCase()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EmployeeDashboard;